import React from 'react';
import type { ControllerFieldState, ControllerRenderProps, FieldPath } from 'react-hook-form';
import {
  Box,
  Checkbox,
  type CheckboxProps,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';

import type { CMS1500FormData } from '../ClaimCMS1500Form';
import { useFieldErrorHelperTextStyling } from '../hooks/useFieldErrorStyling';

interface ClaimCheckboxProps<TFieldName extends FieldPath<CMS1500FormData> = FieldPath<CMS1500FormData>> 
  extends Omit<CheckboxProps, 'checked' | 'onChange'> {
  /** Field props from react-hook-form Controller */
  field: ControllerRenderProps<CMS1500FormData, TFieldName>;
  /** Field state from react-hook-form Controller */
  fieldState?: ControllerFieldState;
  /** Checkbox label */
  label: string;
  /** Override helper text (defaults to error message) */
  helperText?: string;
}

/**
 * ClaimCheckbox - A Checkbox wrapper that automatically applies severity-based error styling
 * 
 * This component handles:
 * - Error severity detection (error/warning/info)
 * - Helper text with appropriate color coding
 * - Consistent styling across all claim form checkboxes
 * 
 * @example
 * ```tsx
 * <Controller
 *   name="outsideLab"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <ClaimCheckbox
 *       field={field}
 *       fieldState={fieldState}
 *       label="Outside Lab"
 *       size="small"
 *     />
 *   )}
 * />
 * ```
 */
export const ClaimCheckbox = <TFieldName extends FieldPath<CMS1500FormData> = FieldPath<CMS1500FormData>>({
  field,
  fieldState,
  label,
  helperText,
  ...checkboxProps
}: ClaimCheckboxProps<TFieldName>): React.ReactElement => {
  const { helperTextSx } = useFieldErrorHelperTextStyling(fieldState);

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            {...checkboxProps}
            /* react-hook-form's own disabled, from `useForm({ disabled })`,
               composed with any the caller passed (GDN-3245). This component
               read NEITHER before, so a read-only CMS-1500 still let you
               toggle Box 20's Outside Lab — a change autosave then declined
               to persist, leaving the screen disagreeing with the claim.
               Must come after the spread so it cannot be overwritten by it. */
            disabled={field.disabled || checkboxProps.disabled}
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            onBlur={field.onBlur}
            name={field.name}
            slotProps={{ input: { ref: field.ref } }}
          />
        }
        label={label}
      />
      {(fieldState?.error || helperText) && (
        <FormHelperText sx={helperTextSx}>
          {helperText ?? fieldState?.error?.message}
        </FormHelperText>
      )}
    </Box>
  );
};

ClaimCheckbox.displayName = 'ClaimCheckbox';
